import {useSuspenseQuery} from '@tanstack/react-query'
import {Link} from '@tanstack/react-router'
import {BlazeConfig} from 'blaze-slider'
import {useTranslations} from 'use-intl'
import {memo, useMemo} from 'react'
import clsx from 'clsx'
import {
    getCompositionImageBaseUrl,
    getDesignImageSrc,
    getDesignImageSrcSet,
    getProductDefaultViewId,
    getProductModelImageSrc,
    getProductModelImageSrcSet,
} from '~/utils/product'
import {productSuggestionsQueryOptions} from '~/client/fn/suggestions'
import {defaultImagesQueryOptions} from '~/client/fn/defaultImages'
import ImageComposition from '~/components/ImageComposition'
import useLocaleParams from '~/hooks/useLocaleParams'
import Carousel from '~/components/common/Carousel'
import useShopConfig from '~/hooks/useShopConfig'
import Price from '~/components/common/Price'
import Card from '~/components/common/Card'
import {ImageFormat} from '~/gql/graphql'

type ProductSuggestionsProps = {
    className?: string
    productId: string
    appearanceId?: string
}

const config: BlazeConfig = {
    all: {
        slidesToShow: 1.25,
        loop: false,
    },
    '(min-width: 520px)': {
        slidesToShow: 2.25,
    },
    '(min-width: 768px)': {
        slidesToShow: 3.25,
        slidesToScroll: 3,
    },
    '(min-width: 1024px)': {
        slidesToShow: 4.25,
        slidesToScroll: 4,
    },
}

export const ProductSuggestions = ({
    productId,
    appearanceId,
    className,
}: ProductSuggestionsProps) => {
    const params = useLocaleParams()
    const t = useTranslations('productDetails.productSuggestions')
    const shopConfig = useShopConfig()
    const {data: defaultImages} = useSuspenseQuery(defaultImagesQueryOptions(params.locale))

    const {data: suggestions} = useSuspenseQuery(
        productSuggestionsQueryOptions(productId, appearanceId, params.locale)
    )

    const suggestionsWithImage = useMemo(() => {
        return suggestions.map((suggestion) => {
            const productTypeImage = defaultImages.find(
                (image) => image.productTypeId === suggestion.productTypeId
            )?.images

            const baseProductImageSrc = `${shopConfig.imageServerEndpoint}/products/${suggestion.vId}`
            const baseDesignImageSrc = getCompositionImageBaseUrl(baseProductImageSrc)!

            return {
                name: suggestion.name,
                price: suggestion.price,
                productId: suggestion.productId,
                appearanceId: suggestion.appearanceId,
                image: {
                    src: getProductModelImageSrc(
                        baseProductImageSrc,
                        suggestion.viewId,
                        suggestion.appearanceId,
                        productTypeImage?.modelId,
                        productTypeImage?.crop
                    ),
                    srcset: getProductModelImageSrcSet(
                        baseProductImageSrc,
                        suggestion.viewId,
                        suggestion.appearanceId,
                        productTypeImage?.modelId,
                        productTypeImage?.crop
                    ),
                    altText: suggestion.name,
                    format: ImageFormat.Png,
                },
                designImage: {
                    src: getDesignImageSrc(
                        baseDesignImageSrc,
                        suggestion.viewId,
                        suggestion.appearanceId
                    ),
                    srcset: getDesignImageSrcSet(
                        baseDesignImageSrc,
                        suggestion.viewId,
                        suggestion.appearanceId
                    ),
                    altText: suggestion.name,
                },
            }
        })
    }, [suggestions, defaultImages, shopConfig.imageServerEndpoint])

    return (
        <div className="relative">
            <Carousel
                className={clsx(className)}
                config={config}
                showDots={false}
                showDisabledArrows={true}
            >
                {suggestionsWithImage.map((suggestion) => (
                    <Link
                        className="block h-full group outline-none"
                        key={suggestion.productId}
                        to="/$lang/products/$productId"
                        params={{...params, productId: suggestion.productId}}
                        search={{appearanceId: suggestion.appearanceId}}
                        aria-label={t('action', {product: suggestion.name})}
                        title={suggestion.name}
                    >
                        <Card className="relative h-full flex flex-col group-hover:bg-gray-700/60 group-focus-visible:bg-gray-700/60 group-focus-visible:ring-2 ring-purple-600 transition duration-200 ease-in-out transform-gpu">
                            <ImageComposition
                                className="overflow-hidden"
                                imageClassName="p-4 rounded scale-100 opacity-100 group-hover:scale-150 group-hover:pointer-events-none group-hover:opacity-40 group-focus-visible:scale-150 group-focus-visible:opacity-40 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out"
                                image={suggestion.image}
                                sizes="500px"
                            />
                            <div className="absolute opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 group-focus-visible:scale-100 group-focus-visible:opacity-100 inset-0 p-4 motion-safe:transition motion-safe:duration-200 motion-safe:ease-in-out">
                                <ImageComposition
                                    className="rounded-md overflow-hidden bg-white"
                                    image={suggestion.designImage}
                                    lazy={true}
                                    sizes="500px"
                                />
                            </div>
                            <div className="flex gap-4 flex-row justify-between p-4 text-sm font-medium">
                                <div className="truncate">{suggestion.name}</div>
                                <Price className="text-nowrap">
                                    {suggestion.price.vatIncluded}
                                </Price>
                            </div>
                        </Card>
                    </Link>
                ))}
            </Carousel>
        </div>
    )
}

export const ProductSuggestionsSkeletonLoader = memo(() => {
    const t = useTranslations('productDetails.productSuggestions')
    return (
        <div>
            <Carousel
                config={{...config, all: {...config.all, draggable: false}}}
                showDots={false}
                showArrows={false}
            >
                {[...new Array(8)].map((_, index) => (
                    <Card key={index} className="p-4" aria-label={t('loading')}>
                        <div className="p-4 aspect-square bg-gray-900/30 animate-pulse rounded w-full" />
                        <div className="m-4 bg-gray-900/30 animate-pulse rounded h-5 w-1/2" />
                    </Card>
                ))}
            </Carousel>
        </div>
    )
})
